// src/stores/useQuestionnaireStore.ts
import { computed, ref } from 'vue';
import { defineStore } from 'pinia';

import { useApiLoading } from '../lib/composables/useApiLoading';
import { useErrorStore } from './useErrorStore';
import { fetchQuestionnaireApi, submitQuestionnaireApi } from '../services/questionnaire.service';
import {
  mapQuestionnaireBlocks,
  toFieldValues,
  toSubmittedAnswers,
} from '../lib/helpers/questionnaireForm';
import type { FormError, FormFieldValue } from '../lib/components/MpfForm/MpfForm.props';
import type { Questionnaire, QuestionnaireAnswers } from '../types/questionnaire';

export type QuestionnaireLoadOutcome =
  | { ok: true }
  | {
      ok: false;
      reason: 'unauthenticated' | 'not-found' | 'cannot-respond' | 'failed';
      code?: string | null;
    };

/**
 * A config producer, not a second renderer. The backend normalises the template
 * to ordered typed blocks; this maps them onto MpfForm's field configs.
 */
export const useQuestionnaireStore = defineStore('questionnaire', () => {
  const { apiLoading, setApiLoading, setApiDone } = useApiLoading();

  const requestId = ref<string | null>(null);
  const questionnaire = ref<Questionnaire | null>(null);
  const answers = ref<QuestionnaireAnswers>({});
  const formErrors = ref<FormError[]>([]);
  const isSubmitted = ref<boolean>(false);

  const isLoaded = computed(() => questionnaire.value !== null);
  const title = computed(() => questionnaire.value?.title ?? '');
  const versionNumber = computed(() => questionnaire.value?.versionNumber ?? '');
  const blocks = computed(() => questionnaire.value?.questionnaire ?? []);
  const canRespond = computed(() => questionnaire.value?.canRespond ?? false);

  // The endpoint returns one flat ordered list, so there is nothing to section by.
  // The `form/:section` route stays harmless: any slug resolves to nothing and the
  // view sends it back to the unsectioned form.
  const isSectioned = computed(() => false);

  function sectionForSlug(slug?: string | null): { slug: null } | null {
    if (!isLoaded.value || slug) return null;

    return { slug: null };
  }

  /** The blocks as form config, with any answers already given put back. */
  function fieldsForSection(slug?: string | null) {
    if (!sectionForSlug(slug)) return [];

    return toFieldValues(mapQuestionnaireBlocks(blocks.value), answers.value);
  }

  function isLastSection(_slug?: string | null): boolean {
    return true;
  }

  function nextSectionSlug(_slug?: string | null): string | null {
    return null;
  }

  function setAnswers(values: Record<string, FormFieldValue>) {
    if (!questionnaire.value) return;

    answers.value = {
      ...answers.value,
      ...toSubmittedAnswers(questionnaire.value, values),
    };
  }

  function reset() {
    requestId.value = null;
    questionnaire.value = null;
    answers.value = {};
    formErrors.value = [];
    isSubmitted.value = false;
  }

  async function load(id: string): Promise<QuestionnaireLoadOutcome> {
    setApiLoading();
    try {
      const response = await fetchQuestionnaireApi(id);

      if (!response.ok || !response.data) {
        // The link is the credential but the answers are not — an unmatched
        // session sends the patient to the demographics challenge, not an error.
        if (response.status === 401 || response.status === 403) {
          return { ok: false, reason: 'unauthenticated' };
        }

        // Also what a session for a different patient gets — the endpoint will not
        // confirm the questionnaire exists.
        if (response.status === 404) {
          return { ok: false, reason: 'not-found', code: 'questionnaire-not-found' };
        }

        return { ok: false, reason: 'failed', code: 'unknown-error' };
      }

      requestId.value = id;
      questionnaire.value = response.data;
      isSubmitted.value = false;

      if (!response.data.canRespond) {
        return {
          ok: false,
          reason: 'cannot-respond',
          code: response.data.cannotRespondReason,
        };
      }

      return { ok: true };
    } finally {
      setApiDone();
    }
  }

  async function submit(): Promise<{ ok: boolean; errors?: unknown }> {
    if (!questionnaire.value || !requestId.value) {
      return { ok: false };
    }

    setApiLoading();
    formErrors.value = [];
    try {
      const result = await submitQuestionnaireApi(
        requestId.value,
        questionnaire.value.versionNumber,
        answers.value,
      );

      if (!result.ok) {
        // The endpoint rejects the whole submit and names no question, so without
        // this the patient sits on the form with no idea why nothing happened.
        const errorStore = useErrorStore();
        errorStore.setErrors(result.errors ?? [{ code: 'unknown-error' }]);
        formErrors.value = [
          {
            name: 'questionnaire-submit',
            errors:
              errorStore.getFirstMessage() ||
              'Your answers could not be submitted. Please check them and try again.',
          },
        ];

        return { ok: false, errors: result.errors };
      }

      isSubmitted.value = true;
      return { ok: true };
    } finally {
      setApiDone();
    }
  }

  return {
    // state
    apiLoading,
    answers,
    formErrors,
    isSubmitted,
    questionnaire,
    requestId,

    // getters
    blocks,
    canRespond,
    isLoaded,
    isSectioned,
    title,
    versionNumber,

    // actions
    fieldsForSection,
    isLastSection,
    load,
    nextSectionSlug,
    reset,
    sectionForSlug,
    setAnswers,
    submit,
  };
});
